import { useCallback, useMemo } from 'react'
import { useAppContext } from '../../../../redux/slices/appContext'
import { localStorageHelper } from '../../../../utils/localStorageHelper'

const SCHEMA_VERSION = 1
const KEY_PREFIX = 'ddpFilter'

function isValidEntry (entry) {
  return !!entry && typeof entry === 'object' && 'value' in entry
}

function isValidBlob (blob) {
  return (
    !!blob &&
    blob.version === SCHEMA_VERSION &&
    typeof blob.selectedOption === 'string' &&
    Array.isArray(blob.selectedValues) &&
    blob.selectedValues.every(isValidEntry) &&
    (blob.variableValue === undefined || Array.isArray(blob.variableValue))
  )
}

export function useFilterPersistence (persistKey) {
  const { clientId, userId } = useAppContext()

  // Includes userId so cached filters don't bleed across users on a shared
  // browser. App-level logout cleanup doesn't touch ddpFilter.* entries; this
  // scoping is the minimum that keeps one user's filter state from being
  // visible/applied in another user's later session on the same machine.
  const composedKey = useMemo(() => {
    if (!persistKey || !clientId || !userId) return null
    return `${KEY_PREFIX}.${persistKey}.${userId}.${clientId}`
  }, [persistKey, userId, clientId])

  const isEnabled = composedKey !== null

  const load = useCallback(() => {
    if (!composedKey) return null
    const blob = localStorageHelper.tryLoad(composedKey)
    if (!isValidBlob(blob)) return null
    // Default variableValue to [] for backward compat with older v1 caches that
    // were saved before the field was added. Consumers can rely on it being an
    // array.
    return {
      ...blob,
      variableValue: Array.isArray(blob.variableValue) ? blob.variableValue : []
    }
  }, [composedKey])

  const save = useCallback((payload) => {
    if (!composedKey) return
    const blob = {
      version: SCHEMA_VERSION,
      selectedOption: payload.selectedOption ?? null,
      selectedValues: payload.selectedValues ?? [],
      variableValue: payload.variableValue ?? []
    }
    const next = JSON.stringify(blob)
    const current = localStorageHelper.maybeLoadRaw(composedKey)
    if (current === next) return
    localStorageHelper.tryStore(composedKey, blob)
  }, [composedKey])

  const clear = useCallback(() => {
    if (!composedKey) return
    localStorageHelper.tryRemove(composedKey)
  }, [composedKey])

  return useMemo(
    () => ({ isEnabled, load, save, clear }),
    [isEnabled, load, save, clear]
  )
}
